/**
 * Service / work-type Autocomplete with per-user MRU and a Most used group (#2672).
 * While the user is typing, query relevance wins and the list is not reshuffled by MRU.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material';
import {
  dropdownGroupLabel,
  DROPDOWN_MRU_SELECTORS,
  orderDropdownOptions,
  pickMostUsedIds,
  useDropdownMru,
} from '@/hooks/useDropdownMru';
import type { Service } from '@/hooks/useServices';

const defaultFilter = createFilterOptions<Service>({ stringify: (option) => option.name });

export interface ServiceCatalogSelectProps {
  options: Service[];
  loading?: boolean;
  value?: Service | null;
  onChange: (service: Service | null) => void;
  label: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  autoFocus?: boolean;
  openOnFocus?: boolean;
  inputValue?: string;
  onInputChange?: (value: string, reason: string) => void;
}

export function ServiceCatalogSelect({
  options,
  loading = false,
  value = null,
  onChange,
  label,
  placeholder,
  size = 'medium',
  autoFocus = false,
  openOnFocus = false,
  inputValue,
  onInputChange,
}: ServiceCatalogSelectProps) {
  const { t } = useTranslation('workorders');
  const mru = useDropdownMru(DROPDOWN_MRU_SELECTORS.service);
  const [uncontrolledInput, setUncontrolledInput] = useState('');
  const query = inputValue !== undefined ? inputValue : uncontrolledInput;
  const mostUsedLabel = t('dropdown.mostUsed');

  const mostUsedIds = useMemo(
    () => pickMostUsedIds(
      options,
      (option) => option.id,
      mru.recentIds,
      (option) => option.usageCount ?? 0,
    ),
    [options, mru.recentIds],
  );

  const grouped = query.trim() === '' && mostUsedIds.size > 0;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={loading}
      value={value}
      onChange={(_event, next) => {
        onChange(next);
        if (next) mru.record(next.id);
      }}
      {...(inputValue !== undefined ? { inputValue } : {})}
      onInputChange={(_event, next, reason) => {
        const nextTyped = reason === 'reset' || reason === 'selectOption' ? '' : next;
        if (inputValue === undefined) setUncontrolledInput(nextTyped);
        onInputChange?.(next, reason);
      }}
      size={size}
      openOnFocus={openOnFocus}
      filterOptions={(opts, state) => {
        const filtered = defaultFilter(opts, state);
        return orderDropdownOptions(filtered, {
          query: state.inputValue,
          recentIds: mru.recentIds,
          getId: (option) => option.id,
          getLabel: (option) => option.name,
          usageCount: (option) => option.usageCount ?? 0,
        });
      }}
      groupBy={grouped
        ? (option) => dropdownGroupLabel(option, {
            query,
            mostUsedIds,
            getId: (item) => item.id,
            mostUsedLabel,
          })
        : undefined}
      renderGroup={grouped
        ? (params) => (
            <li key={params.key}>
              {params.group !== '' && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', px: 2, py: 0.5 }}
                >
                  {params.group}
                </Typography>
              )}
              <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
            </li>
          )
        : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          autoFocus={autoFocus}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.name}</Typography>
            {option.description && (
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            )}
          </Box>
        </li>
      )}
    />
  );
}
